import { ReactNode } from 'react';
import { FormProvider, useFieldArray, UseFieldArrayRemove, UseFormReturn } from 'react-hook-form';
import { FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayUpdate } from 'react-hook-form/dist/types/fieldArray';
import { useTranslation } from 'react-i18next';
import { Definition, getRFQFields, MaterialWithDimensions, ProductType } from '@steelbuy/ts-shared';
import {
    rfqInputs,
    RFQInputs,
    validateThicknessMinPositive,
    validateThicknessMinLessThanMax,
    validateThicknessMaxPositive,
    validateThicknessMaxGreaterThanMin,
} from '../createRFQUtil';
import { RFQType } from '../Schema';

interface RFQItemGroupProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    methods: UseFormReturn<RFQType, any>;
    children(
        headings: RFQInputs,
        append: UseFieldArrayAppend<RFQType>,
        fields: FieldArrayWithId<RFQType>[],
        remove: UseFieldArrayRemove,
        update: UseFieldArrayUpdate<RFQType>
    ): ReactNode;
    material: MaterialWithDimensions;
    indexer: Definition | ProductType;
}

export const RFQItemGroup = ({ methods, children, material, indexer }: RFQItemGroupProps) => {
    const { append, remove, fields, update } = useFieldArray({
        control: methods.control,
        name: `materials.${indexer}`,
    });

    const { t } = useTranslation(['uiDomain']);

    return (
        <FormProvider {...methods}>
            {children(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                getRFQFields(material, t as any).reduce((acc, curr) => {
                    // thicknessMin/thicknessMax have no rfqInputs entry of their own —
                    // fold their rules into the thickness column's rulesMin/rulesMax,
                    // adding cross-field validate functions here in the app layer.
                    if (curr.field === 'thicknessMin') {
                        return {
                            ...acc,
                            thickness: {
                                ...(acc.thickness || rfqInputs.thickness),
                                rulesMin: {
                                    ...curr.rules,
                                    validate: {
                                        numberLargerThan: validateThicknessMinPositive(t),
                                        mustBeLessThan: validateThicknessMinLessThanMax(t),
                                    },
                                },
                            },
                        };
                    }
                    if (curr.field === 'thicknessMax') {
                        return {
                            ...acc,
                            thickness: {
                                ...(acc.thickness || rfqInputs.thickness),
                                rulesMax: {
                                    ...curr.rules,
                                    validate: {
                                        numberLargerThan: validateThicknessMaxPositive(t),
                                        mustBeGreaterThan: validateThicknessMaxGreaterThanMin(t),
                                    },
                                },
                            },
                        };
                    }

                    if (rfqInputs[curr.field] === undefined) return acc;

                    return {
                        ...acc,
                        [curr.field]: { ...rfqInputs[curr.field], rules: curr.rules },
                    };
                }, {} as RFQInputs),
                append,
                fields,
                remove,
                update
            )}
        </FormProvider>
    );
};
